import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTabs } from '../../../../../../../../context/TabsContext';
import { useDocRestoreCover } from '../../../../../../../DocsArea/useDocRestoreCover';

/**
 * Persist / restore `.doc-sheet__body` scrollTop for the active mission doc.
 *
 * Stored on the owning tab as `missionDocScrollById` (persisted with tabs).
 * Each doc keeps its own spot — leave a tab, follow a mention, or go back,
 * and you land where you left off. A doc never opened (or never scrolled)
 * still starts at the top.
 *
 * Flushed in a layout effect when the active tab changes — that runs before
 * the mission-hydrate effect closes the sheet, so the body is still mounted
 * and scrollTop is still readable.
 */
export function useMissionDocSheetScroll(activeDocId, bodyRef) {
  const { activeTabId, tabs, updateTab } = useTabs();
  const ownerTabIdRef = useRef(null);
  const activeDocIdRef = useRef(activeDocId);
  const latestScrollRef = useRef(0);
  const userScrolledRef = useRef(false);
  const restoringRef = useRef(false);
  const tabsRef = useRef(tabs);
  const updateTabRef = useRef(updateTab);
  tabsRef.current = tabs;
  updateTabRef.current = updateTab;

  activeDocIdRef.current = activeDocId;

  const readSaved = (tabId, docId) => {
    if (!tabId || !docId) return null;
    const tab = tabsRef.current.find((t) => t.id === tabId);
    const v = tab?.missionDocScrollById?.[docId];
    return typeof v === 'number' ? v : null;
  };

  const writeSaved = (tabId, docId, scrollTop) => {
    if (!tabId || !docId) return;
    const top = Math.max(0, Math.round(scrollTop));
    const tab = tabsRef.current.find((t) => t.id === tabId);
    const prev = tab?.missionDocScrollById || {};
    if (prev[docId] === top) return;
    // Keep tabsRef in sync so back-to-back writes in the same tick merge
    tabsRef.current = tabsRef.current.map((t) => (
      t.id === tabId
        ? { ...t, missionDocScrollById: { ...prev, [docId]: top } }
        : t
    ));
    updateTabRef.current(
      tabId,
      { missionDocScrollById: { ...prev, [docId]: top } },
    );
  };

  const flushToTab = (tabId, docId, scrollTopOverride) => {
    const resolvedTabId = tabId || ownerTabIdRef.current;
    const resolvedDocId = docId || activeDocIdRef.current;
    if (!resolvedTabId || !resolvedDocId) return;
    const el = bodyRef.current;
    const fromDom = scrollTopOverride == null && el?.isConnected ? el.scrollTop : null;
    const scrollTop =
      scrollTopOverride != null
        ? scrollTopOverride
        : fromDom != null
          ? fromDom
          : latestScrollRef.current;
    // Don't let Strict Mode / pre-restore unmounts clobber a real save with 0
    if (scrollTop <= 0 && !userScrolledRef.current) {
      const existing = readSaved(resolvedTabId, resolvedDocId);
      if (existing != null && existing > 0) return;
    }
    writeSaved(resolvedTabId, resolvedDocId, scrollTop);
    latestScrollRef.current = scrollTop;
  };

  const restoreKey = `${activeTabId || ''}:${activeDocId || ''}`;
  const peeked = activeDocId
    ? readSaved(activeTabId || ownerTabIdRef.current, activeDocId)
    : null;
  const { holdCover, releaseCover } = useDocRestoreCover(restoreKey, peeked > 0);

  // Snapshot owning tab when a doc opens. Always take the current tab so a
  // switch (Tab A/doc1 → Tab B/doc2) doesn't keep writing into Tab A.
  useLayoutEffect(() => {
    if (!activeDocId) return;
    ownerTabIdRef.current = activeTabId || ownerTabIdRef.current;
    userScrolledRef.current = false;
    const saved = readSaved(ownerTabIdRef.current, activeDocId);
    latestScrollRef.current = saved != null ? saved : 0;
    // Body is no longer remounted per doc — pin to top (or saved) immediately
    // so link/back doesn't keep the previous doc's scroll offset.
    const el = bodyRef.current;
    if (el) {
      restoringRef.current = true;
      el.scrollTop = latestScrollRef.current;
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
    }
  }, [activeDocId, activeTabId, bodyRef]);

  // Tab switch: hydrate may close the mission in useEffect (after layout).
  // Capture the doc that was showing — activeDocIdRef already points at the next.
  useLayoutEffect(() => {
    const tabIdAtStart = activeTabId;
    const docIdAtStart = activeDocId;
    return () => {
      flushToTab(tabIdAtStart, docIdAtStart);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabId]);

  // Track scroll; flush the doc you're leaving (tab switch, mention, close).
  useEffect(() => {
    if (!activeDocId) return undefined;

    const docId = activeDocId;
    const tabId = ownerTabIdRef.current || activeTabId;
    let cancelled = false;
    let el = null;
    let timer = null;
    let raf = null;
    // Own snapshot — the shared latestScrollRef is overwritten when the next
    // doc restores, and the DOM already shows that doc by cleanup time.
    const remembered = { current: latestScrollRef.current };

    const onScroll = () => {
      if (!el) return;
      remembered.current = el.scrollTop;
      latestScrollRef.current = el.scrollTop;
      if (restoringRef.current) return;
      userScrolledRef.current = true;
      releaseCover();
      clearTimeout(timer);
      timer = setTimeout(() => {
        writeSaved(tabId, docId, remembered.current);
      }, 50);
    };

    const bind = () => {
      if (cancelled) return;
      el = bodyRef.current;
      if (!el) {
        raf = requestAnimationFrame(bind);
        return;
      }
      remembered.current = el.scrollTop;
      latestScrollRef.current = el.scrollTop;
      el.addEventListener('scroll', onScroll, { passive: true });
    };
    bind();

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(timer);
      el?.removeEventListener('scroll', onScroll);
      flushToTab(tabId, docId, remembered.current);
      if (!activeDocIdRef.current) ownerTabIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId, activeTabId, bodyRef]);

  // Restore; retry until the loaded doc is tall enough
  useEffect(() => {
    if (!activeDocId) return undefined;
    const tabId = ownerTabIdRef.current || activeTabId;
    if (!tabId) return undefined;

    const saved = readSaved(tabId, activeDocId);
    if (saved == null || saved <= 0) return undefined;

    let cancelled = false;
    let settled = false;
    let attempts = 0;
    let raf = null;
    let ro = null;

    const markReady = () => {
      if (settled) return;
      settled = true;
      releaseCover();
    };

    const apply = (el) => {
      if (userScrolledRef.current) {
        markReady();
        return true;
      }
      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      const target = Math.min(saved, max);
      restoringRef.current = true;
      el.scrollTop = target;
      latestScrollRef.current = target;
      requestAnimationFrame(() => {
        restoringRef.current = false;
      });
      return max >= saved;
    };

    const tryRestore = () => {
      if (cancelled || settled) return;
      const el = bodyRef.current;
      if (!el) {
        if (attempts++ < 80) raf = requestAnimationFrame(tryRestore);
        return;
      }

      if (!ro && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => {
          if (cancelled || settled) return;
          const current = bodyRef.current;
          if (!current) return;
          if (apply(current)) markReady();
        });
        ro.observe(el);
      }

      const max = Math.max(0, el.scrollHeight - el.clientHeight);
      if (max < saved && attempts++ < 120) {
        raf = requestAnimationFrame(tryRestore);
        return;
      }

      if (apply(el)) markReady();
      else if (attempts++ < 120) raf = requestAnimationFrame(tryRestore);
      else markReady();
    };

    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(tryRestore);
    });
    const safety = setTimeout(markReady, 2500);

    return () => {
      cancelled = true;
      clearTimeout(safety);
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId, activeTabId, bodyRef, releaseCover]);

  return holdCover;
}

export default useMissionDocSheetScroll;
